import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { money2, errMsg } from "./payrollHelpers";

export default function CommissionAdjustmentModal({ employeeId, employeeName, job, systemAmount, reversal, onClose, onSubmit }) {
  const [saleAmount, setSaleAmount] = useState(job.invoiceAmount);
  const [pct, setPct] = useState(job.commissionPct);
  const [amount, setAmount] = useState(reversal ? 0 : systemAmount);
  const [reason, setReason] = useState(reversal ? "Job cancelled/refunded — reversing commission" : "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const recompute = () => setAmount(Math.round(saleAmount * (pct / 100) * 100) / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      await onSubmit({
        employeeId, category: "COMMISSION", jobId: job.jobId,
        originalAmountOverride: systemAmount,
        adjustmentAmount: Math.round((amount - systemAmount) * 100) / 100,
        reason: reason.trim(), notes: notes.trim(),
        source: reversal ? "reversal" : "manual",
      });
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save commission adjustment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="commission-adjustment-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">{reversal ? "Reverse Commission" : "Edit Commission"} — {employeeName}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[12px] text-slate-500">Job for {job.customer} · System commission: <span className="font-semibold text-slate-700">{money2(systemAmount)}</span></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Sale Amount</label>
              <input type="number" step="0.01" value={saleAmount} onChange={(e) => setSaleAmount(parseFloat(e.target.value) || 0)} onBlur={recompute} data-testid="commission-sale-amount" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Commission %</label>
              <input type="number" step="0.1" value={pct} onChange={(e) => setPct(parseFloat(e.target.value) || 0)} onBlur={recompute} data-testid="commission-pct-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Commission Amount (final)</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} data-testid="commission-amount-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            <div className="text-[11px] text-slate-400 mt-1">Sale × % = {money2(saleAmount * (pct / 100))} — edit directly or use Sale/% then blur to recompute</div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason <span className="text-rose-500">*</span></label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="commission-reason-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Customer negotiated discount" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] resize-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="commission-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving…" : "Save Adjustment"}</button>
        </div>
      </form>
    </div>
  );
}
